import { z } from 'zod';
// Optional additional certificates (doc 16) — every shape for this resource
// (Convention 06): caregiver self-service (/me/caregiver/certificates/*), the
// operator view (+ signed download URL) and the buyer view (name + expiry only).
/** Image / PDF MIME types accepted for certificate scans (same as credentials). */
const AllowedUploadContentTypeSchema = z
    .string()
    .regex(/^(image\/(jpeg|png|webp)|application\/pdf)$/, 'Only JPEG, PNG, WebP, or PDF allowed');
/** Max upload size — 20 MB, aligned with the credential/compliance cap. */
const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
/** A real calendar date (YYYY-MM-DD) — `.date()` rejects 2026-02-31 / 2026-13-01,
 *  which a bare regex let through to a 500 or a silently rolled-over date. */
const IsoDateSchema = z.string().date('Enter a valid date (YYYY-MM-DD)');
/** Matches Prisma's CertificateVerificationStatus. No review step at v1 — every
 *  row is UNVERIFIED; the column exists so a review can be added later (doc 16). */
export const CertificateVerificationStatusSchema = z.enum(['UNVERIFIED', 'VERIFIED']);
export const CertificateUploadUrlRequestSchema = z.object({
    contentType: AllowedUploadContentTypeSchema,
    contentLength: z.number().int().positive().max(MAX_UPLOAD_BYTES),
});
export const CertificateUploadUrlResponseSchema = z.object({
    uploadUrl: z.string().url(),
    objectStorageKey: z.string().min(1).max(500),
    expiresAt: z.string().datetime(),
});
/** POST /me/caregiver/certificates — record an uploaded certificate. `code` is
 *  catalog-validated in the service; `customName` is required for OTHER and
 *  rejected for every other code. */
export const CertificateRecordRequestSchema = z
    .object({
    code: z.string().min(1).max(40),
    customName: z.string().trim().min(2).max(120).optional(),
    issuedAt: IsoDateSchema.optional(),
    expiresAt: IsoDateSchema.optional(),
    objectStorageKey: z.string().min(1).max(500),
})
    .refine((v) => !(v.issuedAt && v.expiresAt) || v.expiresAt >= v.issuedAt, {
    message: 'Expiry date cannot be before the issue date',
    path: ['expiresAt'],
});
export const CertificateResponseSchema = z.object({
    id: z.string().uuid(),
    code: z.string(),
    customName: z.string().nullable(),
    /** Catalog label, or the custom name for OTHER. */
    displayName: z.string(),
    issuedAt: IsoDateSchema.nullable(),
    expiresAt: IsoDateSchema.nullable(),
    /** True when expiresAt is set and already past (Eastern date). */
    isExpired: z.boolean(),
    verificationStatus: CertificateVerificationStatusSchema,
    uploadedAt: z.string().datetime(),
});
export const CertificateCatalogItemSchema = z.object({
    code: z.string(),
    label: z.string(),
    group: z.enum(['HEALTH_AND_SAFETY', 'CARE_TRAINING', 'OTHER']),
    groupLabel: z.string(),
});
/** GET /me/caregiver/certificates — what this care professional may add (CPR
 *  omitted when one of their roles already requires it as a credential) plus
 *  what they've uploaded. */
export const CertificateListResponseSchema = z.object({
    catalog: z.array(CertificateCatalogItemSchema),
    certificates: z.array(CertificateResponseSchema),
});
/** Operator view: the caregiver-facing shape + a short-TTL signed download URL. */
export const AdminCaregiverCertificateDetailSchema = CertificateResponseSchema.extend({
    downloadUrl: z.string().url().nullable(),
    downloadUrlExpiresAt: z.string().datetime().nullable(),
});
/** Buyer view (booked-shift care professional profile): catalog certificates
 *  by NAME + expiry only — the file stays with the operator, who sends proof on
 *  request. OTHER certificates are NOT listed (their name is unreviewed free
 *  text — doc 16 lock 3a); buyers get only `otherCertificateCount`. Uploaded by
 *  the care professional, not verified by TrueCare. */
export const BuyerVisibleAdditionalCertificateSchema = z.object({
    name: z.string(),
    expiresAt: z.string().nullable(), // ISO date (YYYY-MM-DD) or null
    isExpired: z.boolean(),
});
//# sourceMappingURL=certificate.schema.js.map